import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PictureAsPdf, Visibility } from '@mui/icons-material';
import { ConsumoVeiculo, RelatorioCusto, Veiculo, Departamento, Subdepartamento } from '../types';
import { relatorioService } from '../services/relatorioService';
import { veiculoService } from '../services/veiculoService';
import { departamentoService } from '../services/departamentoService';
import { subdepartamentoService } from '../services/subdepartamentoService';
import { getErrorMessage } from '../utils/errors';
import DateMaskField from '../components/DateMaskField';
import PdfViewerDialog from '../components/PdfViewerDialog';

const Relatorios: React.FC = () => {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [subdepartamentos, setSubdepartamentos] = useState<Subdepartamento[]>([]);
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState<number>(0);
  const [subdepartamentoSelecionado, setSubdepartamentoSelecionado] = useState<number>(0);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<number>(0);
  const [consumo, setConsumo] = useState<ConsumoVeiculo | null>(null);
  const [inicio, setInicio] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [fim, setFim] = useState(new Date().toISOString().split('T')[0]);
  const [relatorioCusto, setRelatorioCusto] = useState<RelatorioCusto | null>(null);
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState<string | null>(null);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    loadVeiculos();
    loadDepartamentos();
  }, []);

  const loadDepartamentos = async () => {
    try {
      const [deps, subs] = await Promise.all([
        departamentoService.listarAcessiveis(),
        subdepartamentoService.listar(),
      ]);
      setDepartamentos(deps);
      setSubdepartamentos(subs);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const loadVeiculos = async () => {
    try {
      const data = await veiculoService.listar();
      setVeiculos(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const loadConsumo = async (veiculoId: number) => {
    try {
      const data = await relatorioService.getConsumoVeiculo(veiculoId);
      setConsumo(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const loadRelatorioCusto = async () => {
    try {
      const data = await relatorioService.getRelatorioCustos(inicio, fim);
      setRelatorioCusto(data);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const abrirPdf = (blob: Blob, titulo: string) => {
    const url = window.URL.createObjectURL(blob);
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return { url, title: titulo };
    });
  };

  const fecharPdfViewer = () => {
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const gerarPdf = async (chave: string, titulo: string, requisicao: () => Promise<Blob>) => {
    setErro('');
    setGerando(chave);
    try {
      abrirPdf(await requisicao(), titulo);
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setGerando(null);
    }
  };

  const consumoData = consumo ? [
    { name: consumo.placa, 'Média km/L': consumo.mediaKmPorLitro, 'Total Gasto': consumo.totalGasto },
  ] : [];

  const custoData = relatorioCusto ? [
    { name: 'Abastecimentos', value: relatorioCusto.custoAbastecimentos },
    { name: 'Manutenções', value: relatorioCusto.custoManutencoes },
  ] : [];

  return (
    <Container disableGutters maxWidth={false}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Relatórios
      </Typography>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Consumo por Veículo
            </Typography>
            <TextField
              fullWidth
              select
              label="Selecione o Veículo"
              value={veiculoSelecionado}
              onChange={(e) => {
                const id = Number(e.target.value);
                setVeiculoSelecionado(id);
                if (id) loadConsumo(id);
              }}
              margin="normal"
            >
              <MenuItem value="">Selecione...</MenuItem>
              {veiculos.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.placa} - {v.marca} {v.modelo}
                </MenuItem>
              ))}
            </TextField>

            {consumo && (
              <Box sx={{ mt: 2 }}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h5" color="primary">
                      {consumo.mediaKmPorLitro} km/L
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Média de consumo
                    </Typography>
                  </CardContent>
                </Card>

                {consumo.alerta && (
                  <Card sx={{ mb: 2, bgcolor: '#fff3e0' }}>
                    <CardContent>
                      <Typography variant="body2" color="error">
                        {consumo.alerta}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Total de litros: {consumo.totalLitros?.toLocaleString('pt-BR')} L
                  </Typography>
                  <Typography variant="body2">
                    Quilometragem: {consumo.totalQuilometragem?.toLocaleString('pt-BR')} km
                  </Typography>
                  <Typography variant="body2">
                    Total gasto: R$ {consumo.totalGasto?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            )}

            {consumoData.length > 0 && (
              <Box sx={{ mt: 3, height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={consumoData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Média km/L" fill="#1976d2" />
                    <Bar dataKey="Total Gasto" fill="#388e3c" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Relatório de Custos por Período
            </Typography>
            <DateMaskField
              label="Data Início"
              value={inicio}
              onChange={setInicio}
            />
            <DateMaskField
              label="Data Fim"
              value={fim}
              onChange={setFim}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={loadRelatorioCusto}
              sx={{ mt: 2 }}
            >
              Gerar Relatório
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              sx={{ mt: 1 }}
              disabled={!!gerando}
              onClick={() =>
                gerarPdf('custos', 'Relatório de Custos por Período',
                  () => relatorioService.getCustosPdf(inicio, fim))
              }
            >
              {gerando === 'custos' ? 'Gerando...' : 'Visualizar PDF'}
            </Button>

            {relatorioCusto && (
              <Box sx={{ mt: 3 }}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h5" color="primary">
                      R$ {relatorioCusto.custoTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Custo total no período
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Abastecimentos: R$ {relatorioCusto.custoAbastecimentos?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2">
                    Manutenções: R$ {relatorioCusto.custoManutencoes?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {custoData.length > 0 && (
                  <Box sx={{ mt: 3, height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={custoData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
                        <Bar dataKey="value" fill="#f57c00" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Relatórios em PDF
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Os documentos usam o papel timbrado cadastrado em <strong>Imagens</strong> (ocupando toda a
              página) e as margens definidas em <strong>Margens</strong>.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PictureAsPdf color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">Veículos por organização</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Lista de veículos agrupados por organização.
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      sx={{ mt: 2 }}
                      disabled={!!gerando}
                      onClick={() =>
                        gerarPdf('veic-org', 'Veículos por Organização',
                          relatorioService.getVeiculosPorOrganizacaoPdf)
                      }
                    >
                      {gerando === 'veic-org' ? 'Gerando...' : 'Visualizar PDF'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PictureAsPdf color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">Veículos por departamento</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Lista agrupada por departamento.
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      sx={{ mt: 2 }}
                      disabled={!!gerando}
                      onClick={() =>
                        gerarPdf('veic-dep', 'Veículos por Departamento',
                          relatorioService.getVeiculosPorDepartamentoPdf)
                      }
                    >
                      {gerando === 'veic-dep' ? 'Gerando...' : 'Visualizar PDF'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PictureAsPdf color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">Abastecimentos</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Lista de Abastecimentos
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <DateMaskField
                        label="De"
                        size="small"
                        margin="none"
                        value={inicio}
                        onChange={setInicio}
                      />
                      <DateMaskField
                        label="Até"
                        size="small"
                        margin="none"
                        value={fim}
                        onChange={setFim}
                      />
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      sx={{ mt: 2 }}
                      disabled={!!gerando}
                      onClick={() =>
                        gerarPdf('abast', 'Abastecimentos no Período',
                          () => relatorioService.getAbastecimentosPdf(inicio, fim))
                      }
                    >
                      {gerando === 'abast' ? 'Gerando...' : 'Visualizar PDF'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PictureAsPdf color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">Abastecimentos por Departamento</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Departamento"
                      value={departamentoSelecionado}
                      onChange={(e) => setDepartamentoSelecionado(Number(e.target.value))}
                      sx={{ mb: 1 }}
                    >
                      <MenuItem value={0}>Selecione...</MenuItem>
                      {departamentos.map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>
                      ))}
                    </TextField>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <DateMaskField label="De" size="small" margin="none" value={inicio} onChange={setInicio} />
                      <DateMaskField label="Até" size="small" margin="none" value={fim} onChange={setFim} />
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      sx={{ mt: 2 }}
                      disabled={!!gerando || !departamentoSelecionado}
                      onClick={() =>
                        gerarPdf('abast-dep', 'Abastecimentos por Departamento',
                          () => relatorioService.getAbastecimentosPorDepartamentoPdf(inicio, fim, departamentoSelecionado))
                      }
                    >
                      {gerando === 'abast-dep' ? 'Gerando...' : 'Visualizar PDF'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PictureAsPdf color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">Abastecimentos por Subdepartamento</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Subdepartamento"
                      value={subdepartamentoSelecionado}
                      onChange={(e) => setSubdepartamentoSelecionado(Number(e.target.value))}
                      sx={{ mb: 1 }}
                    >
                      <MenuItem value={0}>Selecione...</MenuItem>
                      {subdepartamentos.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>
                      ))}
                    </TextField>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <DateMaskField label="De" size="small" margin="none" value={inicio} onChange={setInicio} />
                      <DateMaskField label="Até" size="small" margin="none" value={fim} onChange={setFim} />
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Visibility />}
                      sx={{ mt: 2 }}
                      disabled={!!gerando || !subdepartamentoSelecionado}
                      onClick={() =>
                        gerarPdf('abast-sub', 'Abastecimentos por Subdepartamento',
                          () => relatorioService.getAbastecimentosPorSubdepartamentoPdf(inicio, fim, subdepartamentoSelecionado))
                      }
                    >
                      {gerando === 'abast-sub' ? 'Gerando...' : 'Visualizar PDF'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <PdfViewerDialog
        open={!!pdfViewer}
        url={pdfViewer?.url ?? null}
        title={pdfViewer?.title}
        onClose={fecharPdfViewer}
      />
    </Container>
  );
};

export default Relatorios;
